/**
 * @file stripeService.js
 * @description Client-side wrappers around the Stripe edge functions plus
 *   the in-app "checkout engine" — a singleton host pattern that lets any
 *   component fire openStripeCheckout({...}) and have a modal-mounted
 *   Embedded Checkout appear, with no per-caller modal wiring.
 *
 *   Call paths:
 *     1. createCheckoutSession(variantKey, { uiMode? }) — POSTs to
 *        stripe-create-checkout. Default ui_mode is "hosted" (returns url
 *        to redirect to). ui_mode="embedded" returns a client_secret that
 *        feeds @stripe/react-stripe-js's <EmbeddedCheckout/>.
 *     2. redirectToCheckout(variantKey)              — wraps #1 in
 *        hosted mode and navigates the browser.
 *     3. openStripeCheckout({ variantKey, ... })     — opens the embedded
 *        modal via the registered host (see below). Falls back to
 *        redirectToCheckout if no host is mounted, so the engine is
 *        always callable.
 *     4. registerCheckoutHost(handler)               — internal — wired
 *        by <StripeCheckoutHost/> on mount. Returns an unsubscribe fn.
 *     5. createPortalSession() / redirectToPortal()  — Customer Portal.
 *     6. syncStripeProducts({ dryRun })              — admin Stripe sync.
 *
 *   All edge-fn calls are safe no-ops if STRIPE_SECRET_KEY isn't set —
 *   they return { ok: false, error: '...' } cleanly so the UI can show
 *   a "Stripe not configured" message rather than crash.
 *
 * @module lib/stripeService
 */
import { supabase } from "./supabase";
import { getCachedConfig } from "./configService";
import { SPEND_CAP_ERROR, spendCapInfoFromInvokeError } from "./spendCap";
import { functionErrorBody, functionErrorStatus } from "./functionErrorMessage";
import { logCaughtError } from "./errorService";

// stripe-create-checkout answers 409/400 with these codes for a founder (Founder next plan) or an agency that
// already has a subscription. The UI turns each into a plain message instead of a generic failure.
const CHECKOUT_REFUSALS = ["already_subscribed", "founder_end_too_soon", "founder_timing_required"];

// stripe-create-checkout answers 409 { error: "extra_slots_subscription_exists" }
// when the agency already has an extra-slot subscription (a second Checkout would
// start a second subscription and a second charge). Quantity changes go through
// manageExtraSlots instead.
export const EXTRA_SLOTS_EXISTS_ERROR = "extra_slots_subscription_exists";

// The function's own error code from a 409 or 400 answer (body via functionErrorMessage.js) when it is one of
// `codes`, else null.
async function refusalCode(error, codes) {
  const status = functionErrorStatus(error);
  if (status !== 409 && status !== 400) return null;
  const body = await functionErrorBody(error);
  return codes.includes(body?.error) ? body.error : null;
}

// A function-invoke failure becomes { ok:false, error }. A spend cap block (HTTP
// 409) keeps its details under `spendCap` so the UI can explain it. The extras
// duplicate 409 keeps its code so the UI can show a calm message.
async function invokeFailure(error, op = "invoke") {
  const spendCap = await spendCapInfoFromInvokeError(error);
  if (spendCap) return { ok: false, error: SPEND_CAP_ERROR, spendCap };
  const code = await refusalCode(error, [EXTRA_SLOTS_EXISTS_ERROR, ...CHECKOUT_REFUSALS]);
  if (code) return { ok: false, error: code };
  await logInvokeFailure(error, op);
  return { ok: false, error: error.message };
}

// Log an unexpected payments-function failure to Admin > Logs, with the function's own error code (a short token
// such as "no_customer", never card data) to say WHY it failed; the generic invoke message does not.
async function logInvokeFailure(error, op) {
  const body = await functionErrorBody(error);
  logCaughtError(error, `stripe_${op}_failed`, { op, reason: typeof body?.error === "string" ? body.error : undefined });
}

// ── Checkout host registry ─────────────────────────────────────────────────
// Single global slot. The <StripeCheckoutHost/> mounted near the top of the
// React tree calls registerCheckoutHost(handler) on mount. openStripeCheckout
// then routes its opts through that handler. Module-level rather than React
// context so it's callable from non-component code too (e.g. service hooks).
let _hostHandler = null;

/**
 * Register the global checkout-modal host. Called by <StripeCheckoutHost/>.
 * Returns an unsubscribe function for the useEffect cleanup.
 *
 * @param {(opts: { variantKey: string, title?: string, subtitle?: string, onComplete?: Function, onCancel?: Function }) => void} handler
 * @returns {() => void}
 */
export function registerCheckoutHost(handler) {
  _hostHandler = handler;
  return () => {
    if (_hostHandler === handler) _hostHandler = null;
  };
}

/**
 * Create a Stripe Checkout session for the given variant. Behaviour depends
 * on uiMode:
 *
 *   - "hosted"   (default): returns { ok, url } — caller redirects the
 *     browser to that URL. Stripe-hosted page.
 *   - "embedded": returns { ok, client_secret } — caller mounts
 *     <EmbeddedCheckout/> with that client_secret to render the form
 *     in-app. Stripe still owns the form (PCI-compliant iframe), just
 *     visually mounted on our page.
 *
 * @param {string} variantKey - 'pro_monthly', 'pro_annual', 'extra_slot', 'credit_pack_1', etc.
 * @param {{ successUrl?: string, cancelUrl?: string, returnUrl?: string, uiMode?: "hosted"|"embedded" }} [opts]
 * @returns {Promise<{ ok: boolean, url?: string, client_secret?: string, ui_mode?: string, session_id?: string, error?: string }>}
 */
export async function createCheckoutSession(variantKey, opts = {}) {
  if (!variantKey) return { ok: false, error: "variant_key required" };

  const { data, error } = await supabase.functions.invoke("stripe-create-checkout", {
    body: {
      variant_key: variantKey,
      // Only honored for extra_slot variants (first-time multi-slot purchase);
      // ignored server-side for plans / credit packs.
      quantity:    opts.quantity,
      success_url: opts.successUrl,
      cancel_url:  opts.cancelUrl,
      return_url:  opts.returnUrl,
      ui_mode:     opts.uiMode === "embedded" ? "embedded" : "hosted",
      // Founders only ('at_founder_end' | 'now'); the server ignores it for everyone else.
      founder_timing: opts.founderTiming || undefined,
    },
  });
  if (error) return invokeFailure(error, "checkout");
  return data || { ok: false, error: "no_response" };
}

/**
 * Enter or exit Seasonal Pause. Swaps the existing Stripe subscription
 * to/from hibernate_monthly ($9/mo by default). The function records the
 * prior variant_key in agency_profiles.pre_pause_plan when pausing, so
 * resume restores the same plan + cycle automatically.
 *
 * @param {{ resume?: boolean }} [opts] - resume=true exits pause; default enters pause.
 * @returns {Promise<{ ok: boolean, paused?: boolean, resumed?: boolean, target_variant_key?: string, target_plan?: string, current_period_end?: number, noop?: boolean, error?: string, details?: any }>}
 */
export async function pauseSubscription(opts = {}) {
  const { data, error } = await supabase.functions.invoke("stripe-pause-subscription", {
    body: { resume: !!opts.resume },
  });
  if (error) return invokeFailure(error, "pause");
  return data || { ok: false, error: "no_response" };
}

/**
 * Switch the agency's existing Stripe subscription to a different variant
 * (e.g. monthly ↔ annual on the same plan). Stripe swaps the price on the
 * active subscription with proration_behavior=always_invoice — no new
 * subscription is created, the customer is charged or credited the difference
 * automatically.
 *
 * @param {{ targetVariantKey: string }} opts
 * @returns {Promise<{ ok: boolean, current_period_end?: number, interval?: string, target_variant_key?: string, noop?: boolean, error?: string }>}
 */
export async function switchSubscriptionCycle(opts = {}) {
  if (!opts.targetVariantKey) return { ok: false, error: "target_variant_key required" };
  const { data, error } = await supabase.functions.invoke("stripe-update-subscription", {
    body: { target_variant_key: opts.targetVariantKey },
  });
  if (error) return invokeFailure(error, "switch_cycle");
  return data || { ok: false, error: "no_response" };
}

/**
 * Cancel (or undo-cancel) the current agency's Stripe subscription. The
 * subscription stays active until the period end; Stripe fires
 * customer.subscription.deleted at that point, and our webhook downgrades
 * the agency to Starter via change_plan.
 *
 * @param {{ undo?: boolean, mode?: "founder_scheduled" }} [opts] - undo=true reverses a pending cancellation.
 *   mode "founder_scheduled" undoes a Pro scheduled for when Founder access ends (cancelled at once, no charge).
 * @returns {Promise<{ ok: boolean, cancel_at_period_end?: boolean, current_period_end?: number, status?: string, noop?: boolean, error?: string }>}
 */
export async function cancelSubscription(opts = {}) {
  const { data, error } = await supabase.functions.invoke("stripe-cancel-subscription", {
    body: opts.mode ? { mode: opts.mode } : { undo: !!opts.undo },
  });
  if (error) { await logInvokeFailure(error, "cancel"); return { ok: false, error: error.message }; }
  return data || { ok: false, error: "no_response" };
}

/**
 * Open the Stripe Customer Portal for the current agency. Requires a
 * Stripe Customer to already exist (created on first successful Checkout).
 *
 * @param {{ returnUrl?: string }} [opts]
 * @returns {Promise<{ ok: boolean, url?: string, error?: string }>}
 */
export async function createPortalSession(opts = {}) {
  const { data, error } = await supabase.functions.invoke("stripe-create-portal", {
    body: { return_url: opts.returnUrl },
  });
  if (error) { await logInvokeFailure(error, "portal"); return { ok: false, error: error.message }; }
  return data || { ok: false, error: "no_response" };
}

/**
 * Admin-only. Trigger the Stripe price/coupon sync. dryRun returns a diff
 * without applying changes — used by the Control Center "preview" button.
 *
 * @param {{ dryRun?: boolean }} [opts]
 * @returns {Promise<{
 *   ok: boolean,
 *   dry_run?: boolean,
 *   variants_processed?: number,
 *   diff?: Array<{
 *     variant_key: string,
 *     action: 'skip'|'create'|'rotate',
 *     reason?: string,
 *     old_price_id?: string|null,
 *     new_price_id?: string|null,
 *     amount_cents: number,
 *     error?: any,
 *   }>,
 *   error?: string,
 * }>}
 */
export async function syncStripeProducts(opts = {}) {
  const { data, error } = await supabase.functions.invoke("stripe-sync-products", {
    body: { dry_run: !!opts.dryRun, trigger: "manual" },
  });
  if (error) { await logInvokeFailure(error, "sync_products"); return { ok: false, error: error.message }; }
  return data || { ok: false, error: "no_response" };
}

/**
 * Redirect helper — convenience wrapper that does the Checkout call and
 * navigates the browser on success. UI components can just call this.
 *
 * @param {string} variantKey
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function redirectToCheckout(variantKey, opts = {}) {
  // Pass current-origin URLs so local dev (localhost:3000) works as well as
  // production (abacases.com). The edge function falls back to abacases.com
  // if these aren't provided, which is wrong for local testing.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const r = await createCheckoutSession(variantKey, {
    successUrl: `${origin}/billing?status=success`,
    cancelUrl:  `${origin}/billing?status=cancel`,
    founderTiming: opts.founderTiming,
  });
  if (!r.ok || !r.url) return r;
  window.location.assign(r.url);
  return { ok: true };
}

/**
 * ── Engine entrypoint ────────────────────────────────────────────────────
 * Open the embedded Stripe Checkout modal for the given variant. Any
 * component anywhere in the app can call this — it routes through the
 * <StripeCheckoutHost/> mounted near the root of the React tree.
 *
 * Falls back to redirectToCheckout (hosted page) if:
 *   - the host isn't mounted yet, or
 *   - the publishable key isn't configured (REACT_APP_STRIPE_PUBLISHABLE_KEY),
 * so this function is always callable and never crashes the UI.
 *
 * @param {Object} opts
 * @param {string} opts.variantKey - 'pro_monthly', 'credit_pack_5', etc.
 * @param {string} [opts.title]    - Modal header label.
 * @param {string} [opts.subtitle] - Modal sub-text under the title.
 * @param {(result: { session_id: string }) => void} [opts.onComplete] - Fired
 *   when Stripe finalises the payment. The webhook lands canonical state
 *   server-side; callers typically refresh local data in this callback.
 * @param {() => void} [opts.onCancel] - Fired when the modal is dismissed
 *   without completing payment.
 * @param {"at_founder_end"|"now"} [opts.founderTiming] - Founders only: when Pro starts
 *   (sent as `founder_timing`; required by the server for an active founder).
 * @param {(code: string) => void} [opts.onError] - Fired when the server refuses to start Checkout with one of
 *   its known codes (founder_end_too_soon, already_subscribed, founder_timing_required).
 * @returns {Promise<{ ok: boolean, completed?: boolean, cancelled?: boolean, error?: string }>}
 */
export async function openStripeCheckout(opts = {}) {
  if (!opts.variantKey) return { ok: false, error: "variant_key required" };

  // Publishable key now lives in platform_config (admin-tunable). Fall back to
  // the hosted redirect flow when it's missing/malformed so the engine is
  // always callable — no env var required to keep payments working.
  const pk = getCachedConfig("stripe_publishable_key") || "";
  const hasValidPk = typeof pk === "string" && pk.startsWith("pk_");
  if (!_hostHandler || !hasValidPk) {
    // No modal host (or missing publishable key) — fall back to the hosted
    // redirect flow. Callers still get back to /billing?status=success.
    const r = await redirectToCheckout(opts.variantKey, { founderTiming: opts.founderTiming });
    return r.ok ? { ok: true, completed: false, cancelled: false } : r;
  }

  return new Promise((resolve) => {
    _hostHandler({
      variantKey: opts.variantKey,
      quantity:   opts.quantity,
      founderTiming: opts.founderTiming,
      title:      opts.title,
      subtitle:   opts.subtitle,
      onError: (code) => {
        try { opts.onError?.(code); } catch (e) { console.warn("[openStripeCheckout] onError callback threw:", e); }
      },
      onComplete: (result) => {
        // Isolate the caller's callback: a throw in their onComplete must not
        // prevent this promise from resolving (which would hang the await).
        try { opts.onComplete?.(result); } catch (e) { console.warn("[openStripeCheckout] onComplete callback threw:", e); }
        resolve({ ok: true, completed: true, ...result });
      },
      onCancel: () => {
        try { opts.onCancel?.(); } catch (e) { console.warn("[openStripeCheckout] onCancel callback threw:", e); }
        resolve({ ok: true, cancelled: true });
      },
    });
  });
}

/**
 * Redirect helper for the Portal — same pattern.
 *
 * Retained for emergency fallback (e.g., admin wants to send an agency to
 * Stripe Portal for something the in-app modal doesn't cover). The primary
 * payment-method-update path is openPaymentMethodModal() below.
 *
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function redirectToPortal() {
  // Same origin-aware return_url pattern as Checkout above.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const r = await createPortalSession({
    returnUrl: `${origin}/billing`,
  });
  if (!r.ok || !r.url) return r;
  window.location.assign(r.url);
  return { ok: true };
}

/* ─────────────────────────────────────────────────────────────────────────
 * Embedded SetupIntent / payment-method-update flow
 * (in-app replacement for the Stripe Customer Portal redirect)
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Server-side createSetupIntent helper. Calls the
 * stripe-create-setup-intent edge function with the user's JWT and returns
 * the client_secret which the PaymentMethodModal binds to <Elements>.
 *
 * @returns {Promise<{ ok: boolean, client_secret?: string, error?: string }>}
 */
export async function createSetupIntent() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { ok: false, error: "no_session" };
  try {
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-create-setup-intent`;
    const res = await fetch(url, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.ok) {
      logCaughtError(new Error(json?.error || "setup_intent_failed"), "stripe_setup_intent_failed", { httpStatus: res.status });
      return { ok: false, error: json?.error || "setup_intent_failed" };
    }
    return { ok: true, client_secret: json.client_secret, setup_intent_id: json.setup_intent_id };
  } catch (e) {
    logCaughtError(e, "stripe_setup_intent_failed", { step: "network" });
    return { ok: false, error: e?.message || "network_error" };
  }
}

/**
 * Server-side finalize helper. Fires AFTER stripe.confirmSetup() resolves
 * client-side with a payment_method id. Server sets it as default on
 * customer + subscription and immediately retries any open invoices so
 * the dunning grace banner clears without waiting for Stripe's next
 * scheduled retry.
 *
 * @param {string} paymentMethodId
 * @returns {Promise<{ ok: boolean, any_invoice_paid?: boolean, error?: string }>}
 */
export async function finalizePaymentMethod(paymentMethodId) {
  if (!paymentMethodId) return { ok: false, error: "payment_method_id required" };
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { ok: false, error: "no_session" };
  try {
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-finalize-payment-method`;
    const res = await fetch(url, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });
    const json = await res.json().catch(() => ({}));
    if (!json?.ok) {
      logCaughtError(new Error(json?.error || "finalize_failed"), "stripe_finalize_payment_failed", { httpStatus: res.status });
      return { ok: false, error: json?.error || "finalize_failed" };
    }
    return { ok: true, any_invoice_paid: !!json.any_invoice_paid, retried_invoices: json.retried_invoices };
  } catch (e) {
    logCaughtError(e, "stripe_finalize_payment_failed", { step: "network" });
    return { ok: false, error: e?.message || "network_error" };
  }
}

/* ── PaymentMethodModal host registration ────────────────────────────── */
// Same singleton pattern as the Stripe Checkout host. Mount
// <PaymentMethodHost/> once near the App root; any component can fire
// openPaymentMethodModal() and the modal appears.

let _paymentMethodHostHandler = null;

export function registerPaymentMethodHost(handler) {
  _paymentMethodHostHandler = handler;
  return () => { if (_paymentMethodHostHandler === handler) _paymentMethodHostHandler = null; };
}

/**
 * Open the embedded payment-method-update modal. If no host is mounted,
 * falls back to the Stripe Customer Portal redirect so the flow still
 * works (defensive — production should always have the host mounted).
 *
 * @param {object} [opts]
 * @param {Function} [opts.onComplete] — fired after card is saved + retry attempted
 * @param {Function} [opts.onCancel]   — fired if the user closes without saving
 */
export function openPaymentMethodModal(opts = {}) {
  if (_paymentMethodHostHandler) {
    _paymentMethodHostHandler(opts);
    return;
  }
  // Fallback — should not happen if PaymentMethodHost is mounted at root
  // eslint-disable-next-line no-console
  console.warn("[stripeService] No PaymentMethodHost mounted — falling back to Stripe Portal");
  redirectToPortal();
}
